import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetDebate } from "../../api-requests/Get";
import { VoteOnDebate } from "../../api-requests/Updates";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import { UserContext } from "../../context/UserContext";
import Header from "../../components/Header";
import PageLoading from "../../components/PageLoading";
import CommentSection from "../../components/CommentSection";

interface DebateOption {
  id: string;
  option_text: string;
  votes: number;
  percentage: number;
}

interface Debate {
  id: string;
  title: string;
  description?: string;
  totalVotes: number;
  options: DebateOption[];
  userVote?: string;
}

const DebatePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [debate, setDebate] = useState<Debate | null>(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  const fetchDebate = async () => {
    if (!id) return;

    try {
      // Only pass headers if user is actually logged in
      const headers = user?.isloggedin ? getBearerHeaders() : undefined;
      const response = await GetDebate(id, headers);
      console.log("Debate API response:", response);
      console.log("Debate options:", response.debate?.options);
      console.log("User vote:", response.debate?.userVote);
      setDebate(response.debate);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching debate:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebate();
  }, [id, user?.isloggedin]);

  const handleVote = async (optionId: string) => {
    if (!debate || !user || voting || debate.userVote) return;

    setVoting(true);
    try {
      const response = await VoteOnDebate(
        debate.id,
        optionId,
        getBearerHeaders(),
      );
      if (response.success) {
        // Refresh debate data to show updated results
        await fetchDebate();
      } else {
        alert(response.message || "Error submitting vote");
      }
    } catch (error: any) {
      console.error("Error voting:", error);
      const errorMessage =
        error?.response?.data?.message || "Error submitting vote";
      alert(errorMessage);
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  if (!debate) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Debate Not Found</h1>
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg"
            >
              Back to Home
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Debate Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-blue-400 hover:text-blue-300 mb-4 flex items-center"
            >
              ← Back
            </button>
            <h1 className="text-3xl font-bold mb-4">{debate.title}</h1>
            {debate.description && (
              <p className="text-gray-300 text-lg mb-4">{debate.description}</p>
            )}
            <p className="text-gray-400">Total Votes: {debate.totalVotes}</p>
          </div>

          {/* Voting Section */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-6">Cast Your Vote</h2>

            {!user ? (
              <div className="text-center py-8">
                <p className="text-gray-300 mb-4">
                  You need to be logged in to vote on debates
                </p>
                <button
                  onClick={() => navigate("/login")}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-medium"
                >
                  Log In to Vote
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {debate.options.map((option) => {
                  const isUserVote = debate.userVote === option.id;
                  const hasVoted =
                    debate.userVote !== null && debate.userVote !== undefined;

                  console.log(
                    "Option:",
                    option.option_text,
                    "hasVoted:",
                    hasVoted,
                    "userVote:",
                    debate.userVote,
                    "isUserVote:",
                    isUserVote,
                  );

                  return (
                    <div
                      key={option.id}
                      className={`relative border rounded-lg p-4 transition-all ${
                        hasVoted
                          ? isUserVote
                            ? "border-green-500 bg-green-500/10"
                            : "border-gray-600 bg-gray-700"
                          : "border-gray-600 bg-gray-700 hover:border-blue-500 cursor-pointer"
                      }`}
                      onClick={() => {
                        if (!user?.isloggedin) {
                          navigate("/login");
                        } else if (!hasVoted && !voting) {
                          handleVote(option.id);
                        }
                      }}
                    >
                      {/* Vote percentage background */}
                      {hasVoted && (
                        <div
                          className="absolute inset-0 bg-blue-600/20 rounded-lg transition-all duration-500"
                          style={{ width: `${option.percentage}%` }}
                        />
                      )}

                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-lg font-medium">
                            {option.option_text}
                          </span>
                          {isUserVote && (
                            <span className="ml-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                              Your Vote
                            </span>
                          )}
                        </div>

                        {hasVoted && (
                          <div className="text-right">
                            <div className="text-sm font-medium">
                              {option.percentage}%
                            </div>
                            <div className="text-xs text-gray-400">
                              {option.votes} votes
                            </div>
                          </div>
                        )}

                        {!hasVoted && !voting && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!user?.isloggedin) {
                                navigate("/login");
                              } else {
                                handleVote(option.id);
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium"
                          >
                            {!user?.isloggedin ? "Login to Vote" : "Vote"}
                          </button>
                        )}

                        {voting && (
                          <div className="text-gray-400">Voting...</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Results Summary */}
          {debate.userVote !== null && debate.userVote !== undefined && (
            <div className="mt-8 bg-gray-800 p-6 rounded-lg">
              <h3 className="text-lg font-bold mb-4">Debate Results</h3>
              <div className="space-y-2">
                {debate.options
                  .sort((a, b) => b.percentage - a.percentage)
                  .map((option, index) => (
                    <div
                      key={option.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <span
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${
                            index === 0
                              ? "bg-yellow-500 text-black"
                              : "bg-gray-600 text-white"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span>{option.option_text}</span>
                      </div>
                      <span className="font-medium">
                        {option.percentage}% ({option.votes} votes)
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <CommentSection targetType="debate" targetId={id!} className="mt-8" />
        </div>
      </div>
    </>
  );
};

export default DebatePage;
